import { IGif } from "services/types";
import { routes } from "../routes";
import { showNotification } from "./notification";

const isProduction = () => import.meta.env.REACT_APP_ENV === "production";

const getHost = () => isProduction() ? "www.redgifs.com" : "staging.redgifs.com";

const getHostImg = () => isProduction() ? "i.redgifs.com" : "i-staging.redgifs.com";

export const copyLink = (link: string): Promise<void> | void => {
  if (!navigator.clipboard) {
    showNotification({
      title: "Clipboard is not accessible.",
      variant: "error"
    });
    return;
  }

  return navigator.clipboard.writeText(link)
    .then(() => {
      showNotification({
        title: "Link copied!",
        variant: "success"
      });
    })
    .catch(() => {
      showNotification({
        title: "Can't copy link",
        variant: "error"
      });
    });
};

const getShareUser = (userId: string) => {
  return `https://${getHost()}/users/${userId}`;
};

export const copyUserLink = (userId: string): Promise<void> | void => {
  return copyLink(getShareUser(userId));
};

const getShareNiche = (niche: string) => {
  return `https://${getHost()}/niches/${niche}`;
};

export const copyNicheLink = (niche: string): Promise<void> | void => {
  return copyLink(getShareNiche(niche));
};

const getShareCollection = (userName: string, id: string): string => {
  return `https://${getHost()}${routes.collection(userName, id)}`;
};

export const copyCollectionLink = (userName: string, id: string): Promise<void> | void => {
  return copyLink(getShareCollection(userName, id));
};

const getGifLink = (id: string): string => {
  return `https://${getHost()}/watch/${id}`;
};

const getIframeUrl = (id: string) => {
  return `https://${getHost()}/ifr/${id}`;
};

export const getGifFixedEmbedCode = ({ id, width, height }: {id: string; width: number; height: number}) => {
  return [
    `<iframe src='${getIframeUrl(id)}' frameborder='0' scrolling='no' allowfullscreen width='${width}' height='${height}'></iframe>`,
    `<p><a href='${getGifLink(id)}'>via RedGIFs</a></p>`
  ].join("");
};

export const getGifResponsiveEmbedCode = ({ id, width, height }: {id: string; width: number; height: number}) => {
  const ratio = width / height;
  const percent = 100;
  const paddingBottom = (percent / ratio).toFixed(2);
  const divStyle = `position:relative; padding-bottom:${paddingBottom}%`;
  const iframeStyle = "position:absolute; top:0; left:0;";

  return [
    `<div style='${divStyle}'>`,
    `<iframe src='${getIframeUrl(id)}' frameBorder='0' scrolling='no' width='100%' height='100%' style='${iframeStyle}' allowFullScreen></iframe>`,
    "</div>",
    `<p><a href='${getGifLink(id)}'>via RedGIFs</a></p>`,
  ].join("");
};

export const getCollectionEmbedCode = (userName: string, id: string, name: string) => {
  return `<a href="${getShareCollection(userName, id)}">${name} collection</a>`;
};

const getCopyLink = (gif: IGif): string => {
  if (gif.type !== 1) {
    return `https://${getHostImg()}/i/${gif.id}.jpg`;
  } else {
    return getGifLink(gif.id);
  }
};

const getTitle = (gif: IGif, type: string) => {
  const userName = gif.userName ?? null;
  const tags = gif.tags || [];

  if (tags.length > 0 && userName) {
    return `${tags[0]} ${type} by ${userName}`;
  } else if (tags.length > 0) {
    return `${tags[0]} ${type}`;
  } else if (userName) {
    return `A porn ${type} by ${userName}`;
  } else {
    return "Please come up with a description...";
  }
};

const getContentType = (gif: IGif) => gif.type !== 1 ? "image" : "GIF";

export const copyGifLink = (gif: IGif): Promise<void> | void => {
  const link = getCopyLink(gif);
  return copyLink(link);
};

const sendToTwitter = (url: string): void => {
  window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`, "_blank");
};

export const sendGifToTwitter = (id: string): void => {
  sendToTwitter(getGifLink(id));
};

export const sendCollectionToTwitter = (userName: string, id: string): void => {
  sendToTwitter(getShareCollection(userName, id));
};

const sendToReddit = (url: string, title?: string): void => {
  window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${title ?? ""}`, "_blank");
};

export const sendGifToReddit = (gif: IGif) => {
  const type = getContentType(gif);
  const title = getTitle(gif, type);
  const shareLink = getCopyLink(gif);

  sendToReddit(shareLink, title);
};

export const sendCollectionToReddit = (userName: string, id: string) => {
  sendToReddit(getShareCollection(userName, id));
};

// TODO: temporary comment this for testing ease share button for creators img
// const webShareAPI = (
//   shareProps: { header?: string; description?: string; link: string }
// ) => {
//   const { header = "", description = "", link } = shareProps;

//   navigator
//     .share({
//       title: header,
//       text: description,
//       url: link
//     })
//     .then(() => console.debug("Successful share"))
//     .catch((error) => console.debug("Error sharing", error));
// };

// export const sendWebShareAPILink = (gif: IGif) => {
//   const type = getContentType(gif);
//   const header = getTitle(gif, type);
//   const link = getCopyLink(gif);

//   webShareAPI({ header, link });
// };
